/**
 * 上位机授权记录 —— 全局上位机授权码台账。
 *
 * 此前上位机授权码只能进「设备详情」按设备看（且只取最近 100 条），运维要核对「一共发过哪些码、
 * 发给了谁、机器码是多少」只能逐台翻。这一页把它们集中起来，并支持导出交给售后/验收。
 *
 * 数据源 `GET /api/licenses`（分页 + 关键字），导出走 `/api/licenses/export`（不分页，
 * 与列表共用筛选条件）。
 */
import { useState } from 'react'
import { Download, Loader2, Search, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { api, downloadLicensesCsv, useAsync } from '@/lib/api'
import { formatTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/Pagination'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/TableSkeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const PAGE_SIZES = [20, 50, 100] as const

export function LicensesPage() {
  const [keywordInput, setKeywordInput] = useState('')
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [pageSize, setPageSize] = useState<number>(20)
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)

  const { data, error, loading, reload } = useAsync(
    () => api.listLicenses({ page, pageSize, keyword: appliedKeyword }),
    [page, pageSize, appliedKeyword],
  )

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  function applyFilters(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setAppliedKeyword(keywordInput.trim())
    reload()
  }

  function resetFilters() {
    setKeywordInput('')
    setAppliedKeyword('')
    setPage(1)
  }

  /** 导出的是**当前筛选条件下的全部上位机授权记录**，不是这一页。 */
  async function exportCsv() {
    setExporting(true)
    try {
      const {
        blob,
        filename,
        rows: exported,
      } = await downloadLicensesCsv({
        keyword: appliedKeyword || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success(`已导出 ${exported ?? total} 条上位机授权记录`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '导出失败')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="上位机授权记录" />

      <Card>
        <CardContent className="p-5">
          <form
            onSubmit={applyFilters}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_140px_auto] lg:items-end"
          >
            <div className="space-y-1.5">
              <Label htmlFor="license-keyword">关键字</Label>
              <Input
                id="license-keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="设备编号 / 设备名称 / 上位机机器码"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="license-page-size">每页条数</Label>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPage(1)
                }}
              >
                <SelectTrigger id="license-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size} 条 / 页
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Search className="size-4" />
                )}
                查询
              </Button>
              <Button type="button" variant="outline" onClick={resetFilters} disabled={loading}>
                重置
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={exportCsv}
                disabled={loading || exporting}
                title="导出当前筛选条件下的全部上位机授权记录（不受分页影响）"
              >
                {exporting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )}
                导出 CSV
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">加载失败：{error}</span>
          <Button variant="ghost" size="sm" onClick={reload}>
            重试
          </Button>
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[168px]">签发时间</TableHead>
                <TableHead className="w-[130px]">设备编号</TableHead>
                <TableHead className="w-[160px]">设备名称</TableHead>
                <TableHead className="w-[160px]">上位机机器码</TableHead>
                <TableHead>上位机授权码</TableHead>
                <TableHead className="w-[120px]">签发人</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableSkeleton rows={6} cols={6} />
              ) : rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6}>
                    <EmptyState
                      title={appliedKeyword ? '没有符合条件的上位机授权记录' : '暂无上位机授权记录'}
                      action={
                        appliedKeyword ? (
                          <Button variant="outline" size="sm" onClick={resetFilters}>
                            清除筛选条件
                          </Button>
                        ) : undefined
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-[var(--color-muted-foreground)]">
                      {formatTime(row.issued_at)}
                    </TableCell>
                    <TableCell className="font-mono-token truncate text-xs">
                      {row.device_id}
                    </TableCell>
                    <TableCell className="truncate text-sm">{row.device_name}</TableCell>
                    <TableCell className="font-mono-token truncate text-xs">
                      {row.machine_code}
                    </TableCell>
                    <TableCell
                      className="font-mono-token max-w-[360px] truncate text-xs text-[var(--color-muted-foreground)]"
                      title={row.license_key}
                    >
                      {row.license_key}
                    </TableCell>
                    <TableCell className="truncate text-xs">{row.issued_by ?? '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        disabled={loading}
        onChange={setPage}
      />
    </div>
  )
}
